import { useMemo } from 'react'
import {
    APPLICATION_FLOW_CONDITIONS,
    APPLICATION_FLOW_DEFINITIONS,
    type ApplicationFlowActionDefinition,
    type ApplicationFlowConditionId,
    type ApplicationFlowContext,
    type ApplicationFlowDefinition,
    type ApplicationFlowEvent,
    type ApplicationFlowGuardRef,
    type ApplicationFlowId,
    type ApplicationFlowRedirectRule,
    type ApplicationFlowRoute,
    type ApplicationFlowStepDefinition,
    type ApplicationFlowStepId,
    type ApplicationFlowTransition,
    type ApplicationFlowViewEntry,
} from './applicationFlowConfig'

type UseApplicationFlowResolverArgs = {
    flowId: ApplicationFlowId
    context: ApplicationFlowContext
    stepId?: ApplicationFlowStepId
}

export type ResolvedApplicationFlowAction = ApplicationFlowActionDefinition & {
    enabled: boolean
    transition: ApplicationFlowTransition | null
}

export type ApplicationFlowResolverResult = {
    flow: ApplicationFlowDefinition
    step: ApplicationFlowStepDefinition
    stepId: ApplicationFlowStepId
    visibleViews: ApplicationFlowViewEntry[]
    redirect: ApplicationFlowRedirectRule | null
    primaryAction?: ResolvedApplicationFlowAction
    secondaryAction?: ResolvedApplicationFlowAction
    tertiaryAction?: ResolvedApplicationFlowAction
    nextTransition: ApplicationFlowTransition | null
    resolveTransition: (event: ApplicationFlowEvent) => ApplicationFlowTransition | null
}

export function evaluateApplicationFlowGuard(
    guardRef: ApplicationFlowGuardRef | undefined,
    context: ApplicationFlowContext,
): boolean {
    if (!guardRef) return true

    const conditionIds: readonly ApplicationFlowConditionId[] = Array.isArray(guardRef) ? guardRef : [guardRef]
    return conditionIds.every((conditionId) => APPLICATION_FLOW_CONDITIONS[conditionId](context))
}

export function resolveApplicationFlow(flowId: ApplicationFlowId): ApplicationFlowDefinition {
    const flow = APPLICATION_FLOW_DEFINITIONS[flowId]
    if (!flow) {
        throw new Error(`Unknown application flow: ${flowId}`)
    }

    return flow
}

export function resolveApplicationFlowStep(
    flow: ApplicationFlowDefinition,
    stepId?: ApplicationFlowStepId,
): ApplicationFlowStepDefinition {
    const resolvedStepId = stepId ?? flow.initialStep
    const step = flow.steps[resolvedStepId]

    if (!step) {
        throw new Error(`Unknown application flow step: ${resolvedStepId} for flow ${flow.id}`)
    }

    return step
}

export function resolveApplicationFlowStepRoute(step: ApplicationFlowStepDefinition): ApplicationFlowRoute {
    if (!step.ui.route) {
        throw new Error(`Application flow step ${step.id} does not define a route`)
    }

    return step.ui.route
}

export function resolveApplicationFlowEntryRoute(flowId: ApplicationFlowId): ApplicationFlowRoute {
    const flow = resolveApplicationFlow(flowId)
    const step = resolveApplicationFlowStep(flow)

    return resolveApplicationFlowStepRoute(step)
}

export function resolveApplicationFlowTargetRoute(
    flow: ApplicationFlowDefinition,
    target: ApplicationFlowTransition['target'],
): ApplicationFlowRoute | null {
    if (target.kind === 'route') {
        return target.route
    }

    if (target.kind === 'step') {
        return resolveApplicationFlowStepRoute(resolveApplicationFlowStep(flow, target.stepId))
    }

    return null
}

export function resolveApplicationFlowRedirect(
    step: ApplicationFlowStepDefinition,
    context: ApplicationFlowContext,
): ApplicationFlowRedirectRule | null {
    return step.redirects?.find((redirect) => evaluateApplicationFlowGuard(redirect.when, context)) ?? null
}

export function resolveApplicationFlowTransition(
    step: ApplicationFlowStepDefinition,
    event: ApplicationFlowEvent,
    context: ApplicationFlowContext,
): ApplicationFlowTransition | null {
    const transitions = step.on?.[event]
    if (!transitions?.length) return null

    return transitions.find((transition) => evaluateApplicationFlowGuard(transition.when, context)) ?? null
}

export function resolveApplicationFlowVisibleViews(
    step: ApplicationFlowStepDefinition,
    context: ApplicationFlowContext,
): ApplicationFlowViewEntry[] {
    return step.ui.views.filter((view) => evaluateApplicationFlowGuard(view.when, context))
}

export function resolveApplicationFlowAction(
    action: ApplicationFlowActionDefinition | undefined,
    step: ApplicationFlowStepDefinition,
    context: ApplicationFlowContext,
): ResolvedApplicationFlowAction | undefined {
    if (!action) return undefined

    return {
        ...action,
        enabled: evaluateApplicationFlowGuard(action.enabledWhen, context),
        transition: resolveApplicationFlowTransition(step, action.event, context),
    }
}

export function useApplicationFlowResolver({
    flowId,
    context,
    stepId,
}: UseApplicationFlowResolverArgs): ApplicationFlowResolverResult {
    return useMemo(() => {
        const flow = resolveApplicationFlow(flowId)
        const step = resolveApplicationFlowStep(flow, stepId)
        const resolvedStepId = stepId ?? flow.initialStep
        const visibleViews = resolveApplicationFlowVisibleViews(step, context)
        const redirect = resolveApplicationFlowRedirect(step, context)
        const primaryAction = resolveApplicationFlowAction(step.ui.primaryAction, step, context)
        const secondaryAction = resolveApplicationFlowAction(step.ui.secondaryAction, step, context)
        const tertiaryAction = resolveApplicationFlowAction(step.ui.tertiaryAction, step, context)

        return {
            flow,
            step,
            stepId: resolvedStepId,
            visibleViews,
            redirect,
            primaryAction,
            secondaryAction,
            tertiaryAction,
            nextTransition: primaryAction?.transition ?? null,
            resolveTransition: (event: ApplicationFlowEvent) => resolveApplicationFlowTransition(step, event, context),
        }
    }, [context, flowId, stepId])
}
